import React, { useState, useEffect } from "react";
import axios from "../../features/axiosHelper";
import { useNavigate } from "react-router-dom";
import { Box, Container, Button } from "@mui/material";
import "./Login.css";
import ResetPasswordPane from "@/components/Login/ResetPasswordPane";

import EnvironmentSelectionPane from "@/components/Login/EnvironmentSelectionPane";
import ForgotPasswordPane from "@/components/Login/ForgotPasswordPane";
import UserCredentialsPane from "./UserCredentialsPane";

import { useDispatch, useSelector } from "react-redux";
import { selectSignInState } from "../../features/auth/authSlice";
import { ResetSuccess, ShepTextLogo } from "../../ImageRepo";

import {
  setRememberMe,
  selectRememberedUserName,
} from "../../features/auth/rememberMeSlice";
import { useTranslation } from "react-i18next";
import LoadingBar from "@/components/shared/LoadingBar";
import { setUserAndToken } from "../../features/auth/resetPasswordSlice";
import { identify as appcuesIdentify } from "@/features/appcuesHelper";
import VersionInfo from "@/components/Login/VersionInfo";

function getCookie(key) {
  var b = document.cookie.match("(^|;)\\s*" + key + "\\s*=\\s*([^;]+)");
  return b ? b.pop() : "";
}

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const rememberedUser = useSelector(selectRememberedUserName);
  const [Email, setEmail] = useState(null); //SHEP-469
  const [Password, setPassword] = useState(null);
  const [EmailError, setEmailError] = useState(false);
  const [PasswordError, setPasswordError] = useState(false);
  const [CredentialsError, setCredentialsError] = useState(false);
  const [ErrorMessage, setErrorMessage] = useState("");
  const [ForgotPassword, setForgotPassword] = useState(false);
  const [isResetPassword, setIsResetPassword] = useState(false);
  const [userId, setUserId] = useState("");
  const [userDescription, setUserDescription] = useState(null); //SHEP-650
  const [SuccessfullyResetPassword, setSuccessfullyResetPassword] =
    useState(false);
  const [RememberMeCheckBox, setRememberMeCheckBox] = useState(false);
  const [TermsAgreement, setTermsAgreement] = useState(false);
  const [TermsUrl, setTermsUrl] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const signInState = useSelector(selectSignInState);
  const userEmailInput = React.createRef();
  const [EnvironmentSelection, setEnvironmentSelection] = useState(false);
  const [TenantGroup, setTenantGroup] = useState([]);
  const [termsChallenge, setTermsChallenge] = useState(null);
  const { t } = useTranslation("login");
  const [isDisabled, setIsDisabled] = useState(false);

  //SHEP-469: State for the right pane
  const [forgotPasswordPane, setForgotPasswordPane] = useState(false);
  const [environmentSelectionPane, setEnvironmentSelectionPane] =
    useState(false);
  const [userCredentialsPane, setUserCredentialsPane] = useState(true);
  const [darkLoadingBar, setDarkLoadingBar] = useState(false);

  // Password reset detection
  useEffect(() => {
    const queryParameters = new URLSearchParams(window.location.search);
    const userIdParam = queryParameters.get("user");
    const resetPwToken = queryParameters.get("resetToken");
    if (userId != null && resetPwToken != null) {
      dispatch(setUserAndToken({ userId, resetToken: resetPwToken }));
      setUserId(userIdParam);
      setIsResetPassword(true);
      setUserCredentialsPane(false);
    }
  }, [dispatch, userId]);

  useEffect(() => {
    if (signInState) {
      navigate("/homepage");
    }
  }, [signInState, navigate]);

  // Since navigate is an async action, it is possible that sometimes the login page will load before the navigation is executed
  if (signInState) {
    return null;
  }
  const LogIn = async (email, password) => {
    //params only used for resetting the password response
    var validationError = false;
    var isFromReset = email != null && password != null;
    if (Email === "") {
      setEmailError(true);
      validationError = true;
    }
    if (Password === "") {
      setPasswordError(true);
      validationError = true;
    }
    if (validationError && !isFromReset) return;
    setIsDisabled(true);

    const credentials = isFromReset
      ? { Email: email, Password: password }
      : { Email: Email, Password: Password };
    await axios
      .post("/login", credentials)
      .then(async (response) => {
        setIsResetPassword(false);

        localStorage.setItem("refreshToken", response.data.refreshToken);

        dispatch(
          setRememberMe({
            userName: RememberMeCheckBox ? email ?? Email : null,
          })
        );

        // Heap.io: Identify logged in user
        window.heap.identify(response.data.user.id);
        window.heap.addUserProperties({
          Username: response.data.user.username,
          Email: credentials.Email,
        });

        // Appcues: Identify logged in user
        // we need the same id or shared property for intigration between heap and appcues to work
        appcuesIdentify(response.data.user.id);

        // checkTerms(); // SHEP-304: TOS is still non-functional, potential implementation notes in comments
        setTenantGroup(response.data.tenants);
        setEnvironmentSelection(true);
      })
      .catch((err) => {
        setIsDisabled(() => false);
        if (err?.data === "NewPasswordNeeded") {
          alert(
            "Please click 'forgot password?' to reset your account password."
          );
        } else if (err?.data === "CredentialsInvalid") {
          setCredentialsError(true);
        } else {
          console.error(err);
        }
      });
  };

  return (
    <Container
      className="login-container"
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        height: "100vh",
        width: "100vw",
        padding: "30px",
        backgroundColor: "var(--text-primary)",
        overflow: "hidden",
      }}
    >
      <Box
        className="login-left-container"
        sx={{
          width: "50vw",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        {SuccessfullyResetPassword ? (
          <div className="login-container-contents">
            <div className="graphicContainer">
              <img id="resetSuccessGraphic" src={ResetSuccess} />
            </div>
          </div>
        ) : (
          <div className="login-container-contents">
            <img src={ShepTextLogo} alt="Shep Logo" className="shepTextLogo" />
            <VersionInfo />
          </div>
        )}
      </Box>

      <Box
        className="login-right-container"
        sx={{
          width: "50vw",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        {SuccessfullyResetPassword ? (
          <div className="loginContent resetSuccess">
            <div>
              <h1>Password Changed</h1>
              <p>
                You have successfully changed your Shep password. Please keep it
                safe. But if you manage to forget it again do not hesitate to
                ask us for help!
              </p>
            </div>
            <Button onClick={() => navigate("/login")} className="btn-primary">
              Back to Login
            </Button>
          </div>
        ) : (
          <div className="loginContent">
            <p id="errorMessage">{ErrorMessage}</p>
            {environmentSelectionPane && (
              <EnvironmentSelectionPane
                openPopUp={EnvironmentSelection && !TermsAgreement}
                setOpenPopUp={setEnvironmentSelection}
                tenantGroup={TenantGroup}
                userEmail={Email}
                userDescription={userDescription}
                onTermsDeclined={() => {
                  setEnvironmentSelectionPane(false);
                  setForgotPasswordPane(false);
                  setUserCredentialsPane(true);
                  setEmail("");
                  setPassword("");
                }}
                initialTermsChallenge={termsChallenge}
              />
            )}
            {forgotPasswordPane && (
              <ForgotPasswordPane
                setUserCredentialsPane={setUserCredentialsPane}
                setForgotPasswordPane={setForgotPasswordPane}
                setEnvironmentSelectionPane={setEnvironmentSelectionPane}
              />
            )}
            {userCredentialsPane && (
              <UserCredentialsPane
                setUserCredentialsPane={setUserCredentialsPane}
                setForgotPasswordPane={setForgotPasswordPane}
                setEnvironmentSelectionPane={setEnvironmentSelectionPane}
                setTenantGroup={setTenantGroup}
                Email={Email}
                setEmail={setEmail}
                Password={Password}
                setPassword={setPassword}
                setDarkLoadingBar={setDarkLoadingBar}
                RememberMeCheckBox={RememberMeCheckBox}
                setRememberMeCheckBox={setRememberMeCheckBox}
                setUserDescription={setUserDescription}
                setTermsChallenge={setTermsChallenge}
              />
            )}
            {darkLoadingBar && <LoadingBar isDarkMode={true} />}
            {isResetPassword && (
              <ResetPasswordPane
                setUserCredentialsPane={setUserCredentialsPane}
                setForgotPasswordPane={setForgotPasswordPane}
                setEnvironmentSelectionPane={setEnvironmentSelectionPane}
                setTenantGroup={setTenantGroup}
                Email={Email}
                setDarkLoadingBar={setDarkLoadingBar}
                RememberMeCheckBox={RememberMeCheckBox}
                setRememberMeCheckBox={setRememberMeCheckBox}
                setIsResetPassword={setIsResetPassword}
                setEmail={setEmail}
                setPassword={setPassword}
                logIn={LogIn}
                userId={userId}
              />
            )}
          </div>
        )}
      </Box>
    </Container>
  );
};

export default Login;
